import { useEffect, useRef, useState } from 'react';

/**
 * 조건이 바뀔 때마다 결과 수를 다시 센다 — 요청이 겹치면 마지막 것만 반영한다.
 *
 * @param fetchCount 지금 조건으로 결과 수를 받아 오는 함수(최신 것을 기억해 쓰므로 매번 새로 만들어도 된다)
 * @param key 조건을 나타내는 문자열 — 바뀔 때만 다시 센다
 * @param delayMs 칩을 빠르게 여러 번 누를 때 요청을 묶는 시간
 * @returns count — 아직 모르면 null(첫 요청 전·실패), loading — 세는 중
 */
export function useLatestCount(fetchCount: () => Promise<number>, key: string, delayMs: number = 150): { count: number | null; loading: boolean } {
    const [count, setCount] = useState<number | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const fetchRef = useRef(fetchCount);
    fetchRef.current = fetchCount;
    const seqRef = useRef<number>(0);

    useEffect(() => {
        const seq: number = ++seqRef.current;
        setLoading(true);
        const timer: number = window.setTimeout(() => {
            fetchRef.current()
                .then((value: number) => {
                    if (seq === seqRef.current) setCount(value);
                })
                .catch(() => {
                    if (seq === seqRef.current) setCount(null);
                })
                .finally(() => {
                    if (seq === seqRef.current) setLoading(false);
                });
        }, delayMs);
        return () => window.clearTimeout(timer);
    }, [key, delayMs]);

    return { count, loading };
}
